import { useCallback, useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { PlanGeoData } from '../proposition-c/types';
import { cloneSet, newSet } from './engine';
import { FAMILY_KEYS, plansUsing } from './model';
import { applyPlanChanges, migrateProject, type CalculationSection } from './sectionEngine';
import { localCommand } from './localApi';
import { computePermissions, viewerFromSearch } from './permissions';
import type { AnySet, Family, Plan, Project } from './types';

export function useProjects() {
  return useQuery<Project[]>({
    queryKey: ['plan2cost', 'nav-projects', 'v4'],
    queryFn: async () => {
      const res = await fetch('/api/plan/nav-projects');
      if (!res.ok) throw new Error('Impossible de charger les projets');
      return res.json();
    },
    staleTime: Infinity,
  });
}

/** Carte Gentilly de proposition-c (handler MSW existant) : réutilisée pour tous les projets du prototype. */
export function useGeo() {
  return useQuery<PlanGeoData>({
    queryKey: ['plan2cost', 'nav-geo'],
    queryFn: async () => {
      const res = await fetch('/api/plan/projects/gentilly');
      if (!res.ok) throw new Error('Impossible de charger la carte');
      return (await res.json()).geo as PlanGeoData;
    },
    staleTime: Infinity,
  });
}

const now = () => {
  const d = new Date();
  return `${d.toLocaleDateString('fr-FR')} ${d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}`;
};

/** Remplace la liste de jeux d'une famille dans le projet. */
function withSet(p: Project, s: AnySet, fn: (list: AnySet[]) => AnySet[]): Project {
  return { ...p, sets: { ...p.sets, [s.kind]: fn(p.sets[s.kind] as AnySet[]) } };
}

/**
 * Store local : jeux d'hypothèses (6 familles) + plans (lecture seule, relançables).
 * Enregistrer un jeu incrémente sa version → les plans qui l'utilisent deviennent obsolètes.
 */
export function useProjectStore(initial: Project[] | undefined) {
  const [projects, setProjects] = useState<Project[]>([]);
  const ref = useRef<Project[]>([]);
  const sequence = useRef(0);
  const [mutationError, setMutationError] = useState('');
  ref.current = projects;

  useEffect(() => {
    if (initial && projects.length === 0) setProjects(initial.map(migrateProject));
  }, [initial, projects.length]);

  const patch = useCallback((pid: string, fn: (p: Project) => Project) => {
    if (!computePermissions(viewerFromSearch(location.search)).canWrite) {
      setMutationError('Lecture seule : modification refusée.'); return;
    }
    const previous = ref.current.find((p) => p.id === pid);
    if (!previous) return;
    const next = fn(previous);
    ref.current = ref.current.map((p) => p.id === pid ? next : p);
    setProjects(ref.current);
    setMutationError('');
    void localCommand({ project: next }).catch((error: unknown) => {
      setMutationError(error instanceof Error ? error.message : String(error));
      ref.current = ref.current.map((p) => p === next ? previous : p);
      setProjects(ref.current);
    });
  }, []);
  const patchPlan = useCallback((pid: string, id: string, fn: (pl: Plan) => Plan) => {
    patch(pid, (p) => ({ ...p, plans: p.plans.map((pl) => (pl.id === id ? fn(pl) : pl)) }));
  }, [patch]);

  const saveSet = useCallback((pid: string, s: AnySet) => {
    patch(pid, (p) => {
      const next = withSet(p, s, (l) => l.map((x) => (x.id === s.id ? { ...cloneSet(s), version: x.version + 1, updatedAt: now() } : x)));
      return { ...next, plans: next.plans.map((plan) => plan.setIds[s.kind] !== s.id ? plan : {
        ...plan,
        ...(['demande', 'importance', 'trace', 'pointsDepart'].includes(s.kind)
          ? { traceCalculated: false, densityCalculated: false } : {}),
        invalidations: [...new Set([...(plan.invalidations ?? []),
          `${s.name} modifié : ${['developpement', 'phasage'].includes(s.kind) ? 'relancer le phasage' : 'relancer tracé, densité et vérifier le phasage'}.`])],
      }) };
    });
  }, [patch]);

  const createSet = useCallback((pid: string, kind: Family): string => {
    const id = `set-${Date.now()}`;
    patch(pid, (p) => {
      const s = newSet(kind, id, `Nouveau jeu ${p.sets[kind].length + 1}`, now());
      return withSet(p, s, (l) => [...l, s]);
    });
    return id;
  }, [patch]);

  const duplicateSet = useCallback((pid: string, kind: Family, sid: string): string => {
    const id = `set-${Date.now()}`;
    patch(pid, (p) => {
      const src = (p.sets[kind] as AnySet[]).find((s) => s.id === sid);
      return src ? withSet(p, src, (l) => [...l, { ...cloneSet(src), id, name: `${src.name} (copie)`, version: 1, updatedAt: now() }]) : p;
    });
    return id;
  }, [patch]);

  const deleteSet = useCallback((pid: string, kind: Family, sid: string) => {
    patch(pid, (p) => {
      const src = (p.sets[kind] as AnySet[]).find((s) => s.id === sid);
      // Hypothèse par défaut (à valider PO) : un jeu utilisé par un plan, ou le dernier de sa famille, ne se supprime pas.
      return !src || plansUsing(p, kind, sid).length || p.sets[kind].length <= 1 ? p : withSet(p, src, (l) => l.filter((s) => s.id !== sid));
    });
  }, [patch]);

  const createPlan = useCallback((pid: string, name: string, energy: Plan['energy']): string => {
    const id = `plan-${Date.now()}-${sequence.current++}`;
    patch(pid, (p) => {
      const setIds = Object.fromEntries(FAMILY_KEYS.map((family) => [family, p.sets[family][0]?.id ?? ''])) as Plan['setIds'];
      const plan: Plan = {
        id, name, energy, setIds, status: 'draft', progress: 0, updatedAt: now(),
        selectionIds: [], selectionSources: [], buildingIds: [], routeMode: 'auto', routePipeIndexes: [], buildingPhases: {},
      };
      return { ...p, plans: [...p.plans, plan] };
    });
    return id;
  }, [patch]);

  const duplicatePlan = useCallback((pid: string, planId: string): string => {
    const id = `plan-${Date.now()}-${sequence.current++}`;
    patch(pid, (p) => {
      const source = p.plans.find((plan) => plan.id === planId);
      if (!source) return p;
      const copy: Plan = {
        ...source, id, name: `${source.name} (copie)`, status: 'draft', progress: 0,
        updatedAt: now(), snapshot: undefined, results: undefined,
        buildingIds: [...(source.buildingIds ?? [])],
        selectionIds: [...(source.selectionIds ?? [])],
        selectionSources: structuredClone(source.selectionSources ?? []),
        manualBuildingIds: [...(source.manualBuildingIds ?? [])],
        excludedBuildingIds: [...(source.excludedBuildingIds ?? [])],
        pipePhaseOverrides: { ...source.pipePhaseOverrides },
        technicalPoints: structuredClone(source.technicalPoints ?? []),
        routePipeIndexes: [...(source.routePipeIndexes ?? [])],
        buildingPhases: { ...(source.buildingPhases ?? {}) },
      };
      return { ...p, plans: [...p.plans, copy] };
    });
    return id;
  }, [patch]);

  const updatePlan = useCallback((pid: string, planId: string, changes: Partial<Plan>) => {
    patch(pid, (project) => ({ ...project, plans: project.plans.map((plan) =>
      plan.id === planId ? applyPlanChanges(project, plan, changes) : plan) }));
  }, [patch]);

  const updateTechnicalPoints = useCallback((pid: string, technicalPoints: Project['technicalPoints']) => {
    patch(pid, (project) => ({ ...project, technicalPoints, plans: project.plans.map((plan) => ({
      ...plan, traceCalculated: false, densityCalculated: false,
      invalidations: [...new Set([...(plan.invalidations ?? []), 'SST projet modifiés : relancer tracé, densité et vérifier le phasage.'])],
    })) }));
  }, [patch]);

  const createSelection = useCallback((pid: string, name: string, buildingIds: string[]): string => {
    const id = `selection-${Date.now()}-${sequence.current++}`;
    patch(pid, (p) => ({
      ...p,
      selections: [...p.selections, { id, name, buildingIds: [...buildingIds], updatedAt: now() }],
    }));
    return id;
  }, [patch]);

  const updateSelection = useCallback((pid: string, selectionId: string, changes: { name?: string; buildingIds?: string[] }) => {
    patch(pid, (p) => ({
      ...p,
      selections: p.selections.map((selection) => selection.id === selectionId
        ? { ...selection, ...changes, updatedAt: now(), buildingIds: changes.buildingIds ? [...changes.buildingIds] : selection.buildingIds }
        : selection),
    }));
  }, [patch]);

  const deleteSelection = useCallback((pid: string, selectionId: string) => {
    patch(pid, (p) => p.plans.some((plan) => plan.selectionIds?.includes(selectionId)) ? p : ({
      ...p,
      selections: p.selections.filter((selection) => selection.id !== selectionId),
    }));
  }, [patch]);

  /** Une opération locale, même moteur dans MSW et tests ; aucun calcul global. */
  const run = useCallback(async (pid: string, id: string, section: CalculationSection, geo?: PlanGeoData) => {
    if (!computePermissions(viewerFromSearch(location.search)).canWrite) return;
    const startingProject = ref.current.find((project) => project.id === pid);
    const startingPlan = startingProject?.plans.find((plan) => plan.id === id);
    if (!startingProject || !startingPlan || startingPlan.status === 'running') return;
    patchPlan(pid, id, (pl) => ({ ...pl, status: 'running', progress: 0, calculationSection: section, calculationError: undefined }));
    try {
      const response = await localCommand({ project: startingProject, planId: id, section, geo });
      const result = response.plans.find((plan) => plan.id === id);
      if (result) {
        setProjects((current) => current.map((project) => project.id !== pid ? project : {
          ...project, plans: project.plans.map((plan) => {
            if (plan.id !== id) return plan;
            const changedDuringCalculation = project.sets !== startingProject.sets
              || project.technicalPoints !== startingProject.technicalPoints
              || plan.buildingIds !== startingPlan.buildingIds || plan.setIds !== startingPlan.setIds;
            return changedDuringCalculation ? {
              ...plan, status: result.status, progress: 100, results: result.results, snapshot: result.snapshot,
              archives: result.archives, traceCalculated: false, densityCalculated: false, configurationChanged: true,
              invalidations: [...(plan.invalidations ?? []), 'Entrées modifiées pendant le calcul : résultats archivés, sections aval à recalculer.'],
            } : result;
          }),
        }));
      }
    } catch (error) {
      setProjects((current) => current.map((project) => project.id !== pid ? project : {
        ...project, plans: project.plans.map((plan) => plan.id !== id ? plan : {
          ...plan, status: 'failed', calculationError: error instanceof Error ? error.message : String(error),
        }),
      }));
    }
  }, [patchPlan]);

  return {
    projects, saveSet, createSet, duplicateSet, deleteSet, run,
    createPlan, duplicatePlan, updatePlan, createSelection, updateSelection, deleteSelection, updateTechnicalPoints, mutationError,
  };
}
